/**
 * Agent Dashboard — realtime agent run viewer.
 *
 * Layout matches the Viewing Deck (viewing-deck.css):
 *   .obs-bar — LIVE badge, event count, turn
 *   .obs-filters — filter pills
 *   .obs — 2-col grid: .obs-main (event cards) + .obs-side (todos, agents, advisor)
 */

import { esc } from '../../shared/rendering.js'
import { fetchLogRuns, type LogRun } from '../../api/agentLogs.js'
import { apiFetchWithMeta, createEventSource } from '../../api/client.js'
import { action } from '../components/action-delegation.js'
import {
  getTodos,
  getSubagents,
  getAdvisorCalls,
  getTurn,
  getTokens,
  type LiveRun,
  type LiveEvent,
} from './agent_logs_selectors.js'
import { renderObsSide, renderEventCards, getFiltered, fmt } from './agent_logs_render.js'

// ─── State ─────────────────────────────────────────────────────────
let _runs: LogRun[] = []
let _selectedRun: string | null = null
let _liveRuns: LiveRun[] = []
let _events: LiveEvent[] = []
let _eventSource: EventSource | null = null
let _eventFilter: string | null = null

// ─── Entry ─────────────────────────────────────────────────────────
export async function renderAgentLogsView(): Promise<void> {
  const app = document.getElementById('app')
  if (!app) return
  document.title = 'Agent Dashboard — Grizzly'
  app.innerHTML =
    '<div id="agent-dash"><div class="loading"><div class="spinner"></div></div></div>'
  app.focus({ preventScroll: true })
  if (_eventSource) _eventSource.close()
  _liveRuns = []
  _events = []
  // createEventSource stamps the active project and auth token. The previous
  // raw EventSource hardcoded a single tenant id and sent no credentials, so
  // this view only ever worked for one project.
  _eventSource = createEventSource('/api/v2/agent/stream')
  _eventSource.addEventListener('agent', (e) => {
    try {
      const d = JSON.parse(e.data)
      if (d.phase === 'started') {
        _liveRuns.push({ runId: d.runId, kind: d.kind, title: d.title, phase: 'running' })
        if (!_selectedRun) _selectedRun = d.runId
      } else if (d.phase === 'running' && d.runId && d.startedAt) {
        // Server sends active runs on SSE connect with phase='running'
        if (!_liveRuns.some((r) => r.runId === d.runId)) {
          _liveRuns.push({ runId: d.runId, kind: d.kind, title: d.title, phase: 'running' })
        }
        if (!_selectedRun) _selectedRun = d.runId
      } else if (d.phase === 'completed' || d.phase === 'failed')
        _liveRuns = _liveRuns.filter((r) => r.runId !== d.runId)
      else if (d.phase === 'running' && d.event) {
        const ev = parse(d.event as Record<string, unknown>)
        if (ev) _events.push(ev)
      }
      render()
    } catch {
      /* skip */
    }
  })
  apiFetchWithMeta<LiveRun[]>('/api/v2/agent/active')
    .then(function (payload) {
      // apiFetchWithMeta types `data` as present; the raw-fetch fallback that
      // used to guard this is no longer needed.
      const active = payload.data
      active.forEach(function (r: LiveRun) {
        if (
          !_liveRuns.some(function (lr) {
            return lr.runId === r.runId
          })
        )
          _liveRuns.push({ runId: r.runId, kind: r.kind, title: r.title, phase: 'running' })
      })
      if (!_selectedRun && _liveRuns.length > 0) _selectedRun = _liveRuns[0]?.runId ?? null
      render()
    })
    .catch(function () {
      /* active-run polling errors ignored — UI degrades gracefully */
    })
  setInterval(function () {
    void (async function () {
      if (!_selectedRun) return
      try {
        const payload = await apiFetchWithMeta<Record<string, unknown>[]>(
          '/api/v2/agent/events/' + encodeURIComponent(_selectedRun),
        )
        const evs = payload.data
        for (const e of evs) {
          const p = parse(e)
          if (
            p &&
            !_events.some(function (x) {
              return JSON.stringify(x) === JSON.stringify(p)
            })
          )
            _events.push(p)
        }
        render()
      } catch {
        /* ignored — poll will retry */
      }
    })()
  }, 3000)
  _eventSource.addEventListener('ping', () => {
    /* heartbeat — no-op */
  })
  try {
    _runs = await fetchLogRuns()
  } catch {
    _runs = []
  }
  render()
}

function parse(raw: Record<string, unknown>): LiveEvent | null {
  const t = raw.type as string
  if (t === 'turn_start') return { type: 'turn', turn: (raw.turnIndex as number) || 0 }
  if (t === 'turn_end') {
    const u = (raw.message as Record<string, unknown> | undefined)?.usage as
      | Record<string, number>
      | undefined
    return { type: 'usage', usage: u ? `↑${fmt(u.input || 0)} ↓${fmt(u.output || 0)}` : '' }
  }
  if (t === 'thinking_delta') return { type: 'think', think: (raw.delta as string) || '' }
  if (t === 'text_delta') return { type: 'text', text: (raw.delta as string) || '' }
  if (t === 'thinking_end') return { type: 'think_end' }
  if (t === 'toolcall_end') {
    const tc = (raw.toolCall as Record<string, unknown>) || {}
    return {
      type: 'tool',
      tool: (tc.name as string) || '?',
      toolArgs: JSON.stringify(tc.arguments || {}).slice(0, 500),
    }
  }
  if (t === 'tool_execution_end') {
    const r = raw.result as Record<string, unknown> | undefined
    const c = r?.content as { type: string; text?: string }[] | undefined
    return {
      type: 'result',
      toolResult: c?.find((x) => x.type === 'text')?.text?.slice(0, 500) || '',
      toolError: (r?.isError as boolean) || false,
    }
  }
  if (t === 'agent_start') return { type: 'agent_start' }
  if (t === 'agent_end') return { type: 'agent_end' }
  return null
}

// ─── Render ────────────────────────────────────────────────────────
function render(): void {
  const c = document.getElementById('agent-dash')
  if (c) c.innerHTML = renderView()
}

function renderView(): string {
  const lc = _liveRuns.length
  return `<header class="page-header"><div><h1 class="page-title">Agent Dashboard</h1><p class="page-subtitle">${lc} live · ${_runs.length} completed</p></div></header>
  ${renderLiveBar()}
  <div style="display:flex;gap:16px;margin-top:12px">
    <div style="flex:0 0 240px;overflow-y:auto;max-height:calc(100vh - 200px)">${renderSidebar()}</div>
    <div style="flex:1;overflow-y:auto;max-height:calc(100vh - 200px)">${renderMain()}</div>
  </div>`
}

function renderLiveBar(): string {
  if (!_liveRuns.length) return ''
  return `<div class="run-card" style="border-left-color:var(--grn);margin-bottom:12px;padding:8px 14px">
    <div class="run-card-head"><span class="rc-run" style="color:var(--grn)">● LIVE — ${_liveRuns.length} running</span>
    ${_liveRuns.map((r) => `<span class="rc-pill" style="background:${_selectedRun === r.runId ? 'var(--grn-d)' : 'var(--s3)'};color:var(--grn);cursor:pointer;margin-left:8px" ${action('selRun', r.runId)}>${r.kind === 'review' ? '◉' : r.kind === 'semlint' ? '○' : '◎'} ${esc(r.title.slice(0, 30))}</span>`).join('')}
    </div></div>`
}

function renderSidebar(): string {
  const icons: Record<string, string> = { review: '◉', semlint: '○', audit: '◎' }
  return `<div>
    <div style="font-size:9px;color:var(--t4);font-family:var(--mono);margin-bottom:6px;padding:4px 0;border-bottom:1px solid var(--bd)">LIVE</div>
    ${_liveRuns.map((r) => `<div style="padding:3px 4px;cursor:pointer;font-size:9px;color:var(--t2);font-family:var(--mono);${_selectedRun === r.runId ? 'background:var(--s3);border-radius:3px' : ''}" ${action('selRun', r.runId)}>${icons[r.kind] ?? '○'} ${esc(r.title.slice(0, 28))}</div>`).join('')}
    <div style="font-size:9px;color:var(--t4);font-family:var(--mono);margin:12px 0 6px;padding:4px 0;border-bottom:1px solid var(--bd)">COMPLETED</div>
    ${_runs
      .slice(0, 20)
      .map(
        (r) =>
          `<div style="padding:3px 4px;cursor:pointer;font-size:9px;color:var(--t3);font-family:var(--mono);${_selectedRun === r.id ? 'background:var(--s3);border-radius:3px' : ''}" ${action('selCompletedRun', r.id)}>${icons[r.kind] ?? '○'} ${esc(r.kind)} <span style="color:var(--t4)">${esc(r.timestamp.slice(0, 16))}</span></div>`,
      )
      .join('')}
  </div>`
}

function renderMain(): string {
  if (!_selectedRun) return '<div class="empty">Select a live run to see its dashboard</div>'

  const turn = getTurn(_events)
  const tok = getTokens(_events)
  const isRunning = _liveRuns.some((r) => r.runId === _selectedRun)

  // ── Top bar (matches .obs-bar) ──
  const bar = `<div class="obs-bar">
    ${isRunning ? '<span class="live">LIVE</span>' : '<span style="font-family:var(--mono);font-size:11px;color:var(--t3)">Completed</span>'}
    <span class="obs-cnt">${_events.length} events</span>
    <span class="obs-stage">Turn ${turn || '—'}</span>
    <span style="font-family:var(--mono);font-size:10px;color:var(--t4);margin-left:4px">↑${fmt(tok.i)} ↓${fmt(tok.o)}</span>
  </div>`

  // ── Filter pills (matches .obs-filters) ──
  const filters = [
    { k: null, l: 'All' },
    { k: 'think', l: '🧠 Think' },
    { k: 'text', l: '💬 Text' },
    { k: 'tool', l: '🔧 Tools' },
    { k: 'advise', l: '💡 Advise' },
    { k: 'subagent', l: '🔀 Subagent' },
  ]
  const filterBar = `<div class="obs-filters" role="group" style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:8px">${filters
    .map((f) => {
      const active = _eventFilter === f.k
      return `<span class="fp${active ? ' on' : ''}" role="button" tabindex="0" aria-pressed="${active}" ${action('setFilter', f.k || '_all')}>${f.l}</span>`
    })
    .join(
      '',
    )}<span style="margin-left:auto;font-family:var(--mono);font-size:10px;color:var(--t4);padding:2px 0">${getFiltered(_events, _eventFilter).length} of ${_events.length}</span></div>`

  // ── Two-column grid (matches .obs) ──
  const todos = getTodos(_events)
  const subs = getSubagents(_events)
  const adv = getAdvisorCalls(_events)
  const side = renderObsSide(todos, subs, adv, _events)

  // Event cards
  const cards = renderEventCards(_events, _eventFilter)

  return (
    bar +
    filterBar +
    `<div class="obs${todos.length === 0 && subs.length === 0 ? ' no-tasks' : ''}"><div class="obs-main" id="obs-main">${cards || '<div class="empty" style="padding:20px">Waiting for events...</div>'}</div><div class="obs-side" id="obs-side">${side}</div></div>`
  )
}

// ─── Window ────────────────────────────────────────────────────────
const w = window as unknown as Record<string, unknown>
w.selRun = (id: string) => {
  _selectedRun = id
  render()
}
w.selCompletedRun = (id: string) => {
  _selectedRun = id
  render()
}
w.setFilter = (k: string) => {
  _eventFilter = k === '_all' ? null : k
  render()
}
